import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box, Cursor } from '../components/Frame';
import OnboardSteps from '../components/OnboardSteps';
import { newMnemonic, mnemonicToSeed } from '../lib/mnemonic';
import { initSodium, keypairFromSeed, deriveUid } from '../lib/crypto';
import { useStore } from '../lib/store';

const STEPS = [
  { label: '/crypto/entropy',  detail: '256 bits' },
  { label: '/crypto/mnemonic', detail: 'BIP39 / 24w' },
  { label: '/crypto/keypair',  detail: 'Ed25519' },
  { label: '/crypto/uid',      detail: 'derived' },
  { label: '/anti_bot',        detail: 'ok' },
];

export default function GenerateScreen() {
  const navigate = useNavigate();
  const setIdentity = useStore((s) => s.setIdentity);
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [shortUid, setShortUid] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        await new Promise((r) => setTimeout(r, 300));
        if (cancelled) return;
        const mnemonic = newMnemonic();
        setStep(1);

        await new Promise((r) => setTimeout(r, 300));
        if (cancelled) return;
        setStep(2);

        await initSodium();
        const seed = mnemonicToSeed(mnemonic);
        const { publicKey, secretKey } = await keypairFromSeed(seed);
        await new Promise((r) => setTimeout(r, 200));
        if (cancelled) return;
        setStep(3);

        const uid = await deriveUid(publicKey);
        setShortUid(uid);
        await new Promise((r) => setTimeout(r, 200));
        if (cancelled) return;
        setStep(4);

        await new Promise((r) => setTimeout(r, 200));
        if (cancelled) return;
        setStep(5);

        setIdentity({ publicKey, secretKey, uid, mnemonic });
        setDone(true);
      } catch (err) {
        console.error('generate identity failed', err);
        setError(err.message || 'UNKNOWN_ERROR');
      }
    }
    run();
    return () => { cancelled = true; };
  }, [setIdentity]);

  if (error) {
    return (
      <Frame statusLeft="IDENTITY :: ERROR" statusRight="5G">
        <Header title="// CRYPTO_IDENTITY" amber />
        <Box variant="danger">
          <p className="text-xs text-red-500">errore: {error}</p>
        </Box>
        <Footer>
          <Btn onClick={() => navigate('/')}>// back to boot</Btn>
        </Footer>
      </Frame>
    );
  }

  return (
    <Frame statusLeft="IDENTITY :: GENERATE" statusRight="5G">
      <Header title="// La tua identità" crumb="[zero dati]" />
      <OnboardSteps step={1} />

      <p className="text-[15px] text-ink-dim mb-3">
        Sto creando la tua identità.<br />
        Niente email, niente telefono, niente account.
      </p>

      <Box variant="success" className="text-[16px] space-y-1">
        {STEPS.map((s, i) => (
          <div key={i} className="text-ink-dim flex justify-between">
            <span>{s.label} :: {s.detail}</span>
            <span>
              {i < step ? <span className="text-ink">[OK]</span> :
               i === step ? <span className="text-amber">[..]</span> : ''}
            </span>
          </div>
        ))}
      </Box>

      {shortUid && (
        <div className="border border-dashed border-ink-faint p-3 text-center my-3">
          <p className="text-[13px] uppercase tracking-widest text-ink-dim mb-1">// your_uid</p>
          <div className="text-[16px] text-ink text-glow break-all leading-relaxed">
            {shortUid}
          </div>
          <p className="text-[13px] text-ink-dim mt-1">hash(public_key)</p>
        </div>
      )}

      <Box variant="warn" className="text-[13px] text-ink-dim mt-2">
        <span className="text-amber">!</span> Ora ricevi le{' '}
        <span className="text-amber">24 parole</span> di recupero:{' '}
        <span className="text-ink">SONO</span> la tua identità.
      </Box>

      <Footer>
        <Btn
          variant="primary"
          onClick={() => navigate('/mnemonic')}
          disabled={!done}
        >
          {done ? 'Continua ▶' : <>Creazione in corso<Cursor /></>}
        </Btn>
      </Footer>
    </Frame>
  );
}
